import { useAction } from '@reatom/npm-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { Button } from '@blade/ui-kit'
import { ArrowRightIcon } from '@blade/ui-kit'
import { CircularLoader } from '@blade/ui-kit'
import { CurrencyIcon } from '@root/entities/currency/ui'
import { useAppRoutes } from '@root/features/routing'

import { commentsScrollEvent, type CommentsModel } from '../../atoms'

import type { Comment } from '@root/entities/comment'

export type CommentsHistoryThreadHeaderProps = {
  history: Comment.CommentsHistory
  model: CommentsModel
}

export const CommentsHistoryThreadHeader = ({
  history,
}: CommentsHistoryThreadHeaderProps) => {
  const { t } = useTranslation()
  const { navigateToTrading } = useAppRoutes()
  const [isOpenning, setIsOpenning] = useState(false)

  const scrollToComments = useAction(commentsScrollEvent)
  const handleScrollToComments = () => scrollToComments()

  const handleNavigateToTrading = () =>
    navigateToTrading(history.pair.address, history.pair.quote.network)

  const handleLoadAllComments = () => {
    setIsOpenning(true)

    setTimeout(() => {
      handleNavigateToTrading()

      setTimeout(() => {
        setIsOpenning(false)
        handleScrollToComments()
      }, 1000)
    }, 200)
  }

  return (
    <Root>
      <CurrencyIcon
        currency={history.pair.quote}
        size={30}
        pairAddress={history.pair.address}
        css={css`
          cursor: pointer;
        `}
      />
      <Ticker onClick={handleNavigateToTrading}>
        {history.pair.quote.ticker}
      </Ticker>
      <Middle>
        <Total onClick={handleLoadAllComments}>
          {t('comment.comments_all', {
            count: history.totalCount,
          })}
          {isOpenning ? <CircularLoader size={12} /> : undefined}
        </Total>
      </Middle>
      <Actions onClick={handleNavigateToTrading}>
        <span>{t('currency.view_token_page')}</span>
        <Button
          type="button"
          variant="secondary"
          iconLeft={<ArrowRightIcon size={16} />}
          size="xxs"
        />
      </Actions>
    </Root>
  )
}

const Root = styled.div`
  background: var(--backgroundA-2);

  display: grid;
  grid-template-columns: auto auto 1fr auto;
  align-items: center;

  height: 42px;
  border-radius: 8px;
  padding-inline: 8px;
  gap: 10px;
`

const Ticker = styled.span`
  color: var(--text-12);

  font-size: 12px;
  font-weight: 700;
  line-height: normal;
  letter-spacing: 0.48px;
  text-transform: uppercase;
  cursor: pointer;
`
const Middle = styled.div`
  display: flex;
`

const Total = styled.div`
  color: var(--text-10);
  cursor: pointer;

  font-size: 11px;
  font-weight: 500;
  line-height: normal;

  display: inline-flex;
  justify-content: start;
  align-items: center;
  gap: 4px;
`

const Actions = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;

  gap: 6px;
  cursor: pointer;

  span {
    color: var(--text-10);

    font-size: 11px;
    font-weight: 500;
    line-height: 16px;
  }
`
